import { makeAutoObservable, runInAction } from 'mobx'

import { AlertProps } from '@mui/material'

import {
  AccountNotificationTypes,
  BudgetNotificationTypes,
  InsightNotificationTypes,
} from '../types'

import type {
  DetailedNotificationProfile,
  Device,
  NotificationProfile,
  SettingsCopy,
  TransactionRule,
  User,
  UserCommunicationProfile,
} from '../types'

import { SettingsApi, TransactionApi } from '../api'
import { augmentNotificationProfiles } from '../utils/SettingsUtil'

import { GlobalStore } from './GlobalStore'

interface SettingsAlertProps {
  message: string
  severity: AlertProps['severity']
}

export class SettingsStore {
  globalStore: GlobalStore
  api: SettingsApi = new SettingsApi('/', '')
  transactionApi: TransactionApi = new TransactionApi('/', '')

  alert: SettingsAlertProps = { message: '', severity: undefined }
  devices: Device[] = []
  notificationProfiles: NotificationProfile[] = []
  selectedRule: TransactionRule | undefined = undefined
  transactionRules: TransactionRule[] = []

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore
    this.api = new SettingsApi(globalStore.endpoint, globalStore.sessionToken, globalStore.onError)
    this.transactionApi = new TransactionApi(
      globalStore.endpoint,
      globalStore.sessionToken,
      globalStore.onError,
    )

    makeAutoObservable(this)
  }

  get accountProfiles() {
    const ccp = this.globalStore.appDataStore.clientCommunicationProfile

    // TODO: There has to be a better way to do this, but for now, it works
    return this.detailedNotificationProfiles.filter(
      (np) =>
        (np.notification_type === AccountNotificationTypes.DebtPaymentReminder &&
          ccp.debt_payment_reminder_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.AccountBalanceLow &&
          ccp.low_balance_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.AccountDepositLarge &&
          ccp.large_deposit_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.TransactionExpenseLarge &&
          ccp.large_expense_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.TransactionFeeCharge &&
          ccp.transaction_fee_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.InternationalTransaction &&
          ccp.transaction_is_international_notifications_enabled) ||
        (np.notification_type === AccountNotificationTypes.AccountDepositLargePosted &&
          ccp.large_deposit_posted_notifications_enabled),
    )
  }

  get budgetProfiles() {
    const ccp = this.globalStore.appDataStore.clientCommunicationProfile

    // TODO: There has to be a better way to do this, but for now, it works
    return this.detailedNotificationProfiles.filter(
      (np) =>
        (np.notification_type === BudgetNotificationTypes.BudgetExceeded &&
          ccp.budget_exceeded_notifications_enabled) ||
        (np.notification_type === BudgetNotificationTypes.BudgetOffTarget &&
          ccp.budget_off_target_notifications_enabled),
    )
  }

  get insightsProfiles() {
    return this.detailedNotificationProfiles.filter((np) =>
      Object.values(InsightNotificationTypes).includes(np.notification_type),
    )
  }

  get clientCommunicationProfile() {
    return this.globalStore.appDataStore.clientCommunicationProfile
  }

  get copy(): SettingsCopy {
    return this.globalStore.copyStore.copy.settings
  }

  get detailedNotificationProfiles(): DetailedNotificationProfile[] {
    // Don't attempt to augment profile if global store is not initialized
    if (!this.globalStore.isInitialized) return []

    return augmentNotificationProfiles(
      this.globalStore.accountStore.accounts,
      this.clientCommunicationProfile,
      this.copy,
      this.notificationProfiles,
      this.userCommunicationsProfile,
      this.globalStore.userStore.user,
    )
  }

  get userCommunicationsProfile() {
    return this.globalStore.appDataStore.userCommunicationProfile
  }

  deleteDevice = async (guid: string) => {
    try {
      await this.api.deleteDevice(guid)

      const index = this.devices.findIndex((d) => d.guid === guid)
      if (index >= 0) {
        runInAction(() => {
          this.devices.splice(index, 1)
        })
      }

      return { success: true }
    } catch (error) {
      console.error('Error deleting device:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      return { success: false }
    }
  }

  deleteTransactionRule = async () => {
    if (!this.selectedRule) {
      return
    }
    try {
      await this.transactionApi.removeTransactionRule(this.selectedRule?.guid)
      this.setAlert(this.copy.delete_rule_success, 'success')
      await this.loadTransactionRules()
    } catch (error) {
      console.error('Error deleting transaction rule:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.delete_rule_error, 'error')
    }
  }

  generateDeviceToken = async () => {
    try {
      return await this.api.generateDeviceToken()
    } catch (error) {
      console.error('Error generating device token:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
    return undefined
  }

  loadDevices = async () => {
    try {
      const devices = await this.api.getDevices()

      runInAction(() => {
        this.devices = devices
      })
    } catch (error) {
      console.error('Error loading devices:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  loadNotificationProfiles = async () => {
    try {
      const profiles = await this.api.getNotificationProfiles()

      runInAction(() => {
        this.notificationProfiles = profiles
      })
    } catch (error) {
      console.error('Error loading notification profiles:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  loadTransactionRules = async () => {
    try {
      const rules = await this.transactionApi.getTransactionRules()

      runInAction(() => {
        this.transactionRules = rules
      })
    } catch (error) {
      console.error('Error loading transaction rules:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  sendEmailVerification = async () => {
    try {
      await this.api.sendEmailVerification()
      this.setAlert(this.copy.verification_sent_success, 'success')
    } catch (error) {
      console.error('Error sending email verification:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.verification_sent_error, 'error')
    }
  }

  sendPhoneVerification = async () => {
    try {
      await this.api.sendPhoneVerification()
      this.setAlert(this.copy.verification_sent_success, 'success')
    } catch (error) {
      console.error('Error sending phone verification:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.verification_sent_error, 'error')
    }
  }

  setAlert = (message: string, severity: SettingsAlertProps['severity'] = undefined) =>
    (this.alert = { message, severity })

  setSelectedRule = (rule?: TransactionRule) => {
    this.selectedRule = rule
  }

  updatePassword = async (params: { new_password: string; new_password_again: string }) => {
    try {
      await this.api.updatePassword(params)
      this.setAlert(this.copy.password_updated_success, 'success')
    } catch (error) {
      console.error('Error updating password:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.password_updated_error, 'error')
    }
  }

  updateDevice = async (device: Device) => {
    try {
      const updatedDevice = await this.api.updateDevice(device)

      const index = this.devices.findIndex((d) => d.guid === device.guid)
      if (index >= 0) {
        runInAction(() => {
          this.devices[index] = updatedDevice
        })
      }
      return { success: true }
    } catch (error) {
      console.error('Error updating device:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      return { success: false }
    }
  }

  updateNotificationProfile = async (profile: NotificationProfile) => {
    try {
      const updatedProfile = await this.api.updateNotificationProfile(profile)

      const index = this.notificationProfiles.findIndex((p) => p.guid === profile.guid)
      if (index >= 0) {
        runInAction(() => {
          this.notificationProfiles[index] = updatedProfile
        })
      }
    } catch (error) {
      console.error('Error updating notification profile:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  updateRuleCategory = async (rule: TransactionRule) => {
    try {
      await this.transactionApi.updateTransactionRule(rule)
      await this.loadTransactionRules()
    } catch (error) {
      console.error('Error updating rule category:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  updateRulePayee = async (rule: TransactionRule) => {
    try {
      await this.transactionApi.updateTransactionRule(rule)
      this.setAlert(this.copy.edit_payee_success, 'success')
      await this.loadTransactionRules()
    } catch (error) {
      console.error('Error updating rule payee:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.edit_payee_error, 'error')
    }
  }

  updateUser = async (user: User) => {
    try {
      await this.globalStore.userStore.updateUser(user)
      this.setAlert(this.copy.information_updated_success, 'success')
    } catch {
      // Error is already logged and captured in UserStore.updateUser
      this.setAlert(this.copy.information_updated_error, 'error')
    }
  }

  updateUserCommunicationProfile = async (profile: UserCommunicationProfile) => {
    try {
      const updatedProfile = await this.api.updateUserCommunicationProfile(profile)

      this.globalStore.appDataStore.setUserCommunicationProfile(updatedProfile)
    } catch (error) {
      console.error('Error occurred while updating user communication profile:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
    }
  }

  verifyPhoneToken = async (sms_verification_token: string) => {
    try {
      const { verified } = await this.api.verifyPhoneToken(sms_verification_token)
      if (!verified) {
        throw new Error()
      }
      this.setAlert(this.copy.verified_phone_success, 'success')
    } catch (error) {
      console.error('Error verifying phone token:', error)
      this.globalStore.captureException(error, { store: 'SettingsStore' })
      this.setAlert(this.copy.verified_phone_error, 'error')
    }
  }
}
